// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, Box,
    List, ListItem, ListItemText, Collapse, IconButton,
    Card, CardContent, Accordion, AccordionSummary, AccordionDetails, Chip
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useGetApi, usePostApi } from '../data/hooks/api';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { AccountDetail, ProjectDetail, CaseGroup, CaseLink, EolProduct, ProjectHealthStatus } from '../data/utils/types';
import LinearLoading from './LinearLoading';
import { ItemNotFoundComponent } from '../pages/ItemNotFound';
import HealthStatusCell from './ReviewStatusCell';
import ActionItemsSection from './ActionItemsSection';
import { green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
 
// Props for the RiskCell component
interface RiskCellProps {
    project: ProjectDetail;
    indicator: keyof ProjectDetail;
    detailData?: any[];
    renderDetailItem: (item: any, index: number) => React.ReactNode;
}

const RiskCell: React.FC<RiskCellProps> = ({
project, indicator, detailData, renderDetailItem
}) => {
    const [open, setOpen] = useState(false);
    
    const hasDetails = (detailData?.length || 0) > 0;
    
    let statusIcon: React.ReactNode = null;
    let statusText: string = "";
    let showToggle = false;

    switch (indicator) {
        // CASE 1 - Go Live Status  
        case 'goLiveStatus': {
            const { status, isRisk } = project.goLiveStatus;
            statusText = status;
            
            if (isRisk) {
                statusIcon = <StatusIndicator isRisk={true} />;  
            } else if (status.includes("Hasn't Gone Live Since")) {
                statusIcon = <WarningIcon color="warning" />;  
            } else {
                statusIcon = <StatusIndicator isRisk={false} />;  
            }
            // show if there are details 
            showToggle = hasDetails; 
            break;
        }

        // CASE 2 - Recent Cases 
        case 'hasRecentCases': {
            const hasCases = project.hasRecentCases;
            const count = project.totalRecentCases;

            if (hasCases) {
                statusIcon = <StatusIndicator isRisk={false} />;
                statusText = `${count} Cases`;
                showToggle = true;  
            } else {
                statusIcon = <StatusIndicator isRisk={true} />;
                statusText = "None";
                showToggle = false;
            }
            break;
        }

        // CASE 3 - EOL Product 
        case 'isUsingEolProduct': {
            const isUsing = project.isUsingEolProduct;
            
            if (isUsing) {
                statusIcon = <StatusIndicator isRisk={true} />;
                statusText = "In Use";
                showToggle = hasDetails;
            } else {
                statusIcon = <StatusIndicator isRisk={false} />;
                statusText = "No";
                showToggle = false;
            }
            break;
        }

        // CASE 4: Default for Abandoned Migrations, Delays, Escalations
        default: {
            // Safely cast to boolean to check if risk exists
            const riskExists = !!project[indicator]; 
            const count = detailData?.length || 0;

            if (riskExists) {
                statusIcon = <StatusIndicator isRisk={true} />;
                statusText = hasDetails ? `${count} Exist` : "Exist";
                showToggle = hasDetails;
            } else {
                statusIcon = <StatusIndicator isRisk={false} />;
                statusText = "None";
                showToggle = false;
            }
            break;
        }
    }

    // Render the table cell with status icon, text, and optional expandable details
    return (
        <TableCell>
            <Box display="flex" alignItems="center">
                {statusIcon}
                <Typography variant="body2" sx={{ ml: 1 }}>{statusText}</Typography>
                {showToggle && (
                     <IconButton size="small" onClick={() => setOpen(!open)}>
                         {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                     </IconButton>
                )}
            </Box>
            {showToggle && (
                 <Collapse in={open} timeout="auto" unmountOnExit>
                     <List dense disablePadding sx={{ pl: 2 }}>
                         {detailData!.map(renderDetailItem)}
                     </List>
                 </Collapse>
            )}
        </TableCell>
    );
};

interface CustomerHealthDetailTableProps {
    accountId: string;
    onAccountNameLoaded?: (name: string) => void;
}

// Main table component for customer health details that displays all risk indicators for all projects
export const CustomerHealthDetailTable: React.FC<CustomerHealthDetailTableProps> = ({ accountId, onAccountNameLoaded }) => {
    // Fetch account data from API
    const { data: accountData, loading, error, getApiData } = useGetApi<AccountDetail>({
        url: ""
    });
    const { data: healthStatusData, getApiData: getHealthStatusData } = useGetApi<ProjectHealthStatus[]>({
        url: ""
    });
    const { postApiData: initHealthTracking } = usePostApi<void>({
        url: "",
        headers: { "accept": "application/json" }
    });
    // Fetch data when accountId changes
    useEffect(() => {
        if (accountId) {
            const detailApiUrl = RESOURCE_SERVER_URLS[0] + `/customer-health/accounts/${accountId}`;
            getApiData(detailApiUrl);
            getHealthStatusData(RESOURCE_SERVER_URLS[0] + `/customer-health/accounts/${accountId}/health-status`);
        }
    }, [accountId]);

    // Once the project list is known, ensure all projects have a health status row in the DB.
    // INSERT IGNORE means existing rows are never touched — this is fully idempotent.
    useEffect(() => {
        if (accountData?.customerProjects && accountData.customerProjects.length > 0) {
            const projectSysIds = accountData.customerProjects.map(p => p.sysId);
            initHealthTracking(
                { projectSysIds },
                RESOURCE_SERVER_URLS[0] + `/customer-health/accounts/${accountId}/init-health-tracking`
            );
        }
    }, [accountData]);

    const handleHealthStatusChanged = () => {
        getHealthStatusData(RESOURCE_SERVER_URLS[0] + `/customer-health/accounts/${accountId}/health-status`);
    };

    const [addForProject, setAddForProject] = useState<string | null>(null);

    const getProjectHealthStatus = (projectSysId: string): ProjectHealthStatus | null => {
        return healthStatusData?.find(s => s.projectSysId === projectSysId) ?? null;
    };
    useEffect(() => {
        if (accountData?.accountName && onAccountNameLoaded) {
            onAccountNameLoaded(accountData.accountName);
        }
    }, [accountData, onAccountNameLoaded]);

    if (loading) return <LinearLoading />;

    if (error) {
        if (error.status === 500) {
             return <ItemNotFoundComponent />;
        }
    }
    // Handle case where no account data is returned
    if (!accountData) return <Typography sx={{ mt: 3, textAlign: 'center' }}>No data available for this account.</Typography>;

    // Handle case where there are no projects for the account
    if (!accountData.customerProjects || accountData.customerProjects.length === 0) return <Typography sx={{ mt: 3, textAlign: 'center' }}>No projects found for this account.</Typography>;

    const riskTypesCount = [
        accountData.customerProjects.some(p => !p.hasRecentCases),
        accountData.customerProjects.some(p => p.hasAbandonedCases),
        accountData.customerProjects.some(p => p.isUsingEolProduct),
        accountData.customerProjects.some(p => p.hasMigrationDelays),
        accountData.customerProjects.some(p => p.hasEscalatedCases),
        accountData.customerProjects.some(p => p.goLiveStatus.isRisk),
    ].filter(Boolean).length;

    return (
        <>
        <Box sx={{ mt: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#424242', lineHeight: 1.3 }}>
                        Project Health Overview
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                        Review and update the health status of each project
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                {accountData.customerProjects.map(project => {
                    const phs = getProjectHealthStatus(project.sysId);
                    const status = phs?.healthStatus?.status ?? 'to_be_reviewed';
                    const borderColor = status === 'at_risk' ? '#D32F2F'
                        : status === 'healthy' ? '#2E7D32'
                        : '#E65100';
                    return (
                        <Card
                            key={project.sysId}
                            elevation={1}
                            sx={{
                                minWidth: 320,
                                maxWidth: 400,
                                flexShrink: 0,
                                borderLeft: `4px solid ${borderColor}`,
                                backgroundColor: '#fff',
                            }}
                        >
                            <CardContent sx={{ p: 3, pb: '24px !important' }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.5 }}>
                                    {project.name}
                                </Typography>
                                <HealthStatusCell
                                    project={project}
                                    accountSysId={accountId}
                                    healthStatus={phs}
                                    onStatusChanged={handleHealthStatusChanged}
                                    onAddActionItem={() => {
                                        setAddForProject(project.sysId);
                                        setTimeout(() => document.getElementById('action-items-section')?.scrollIntoView({ behavior: 'smooth' }), 150);
                                    }}
                                />
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Box>
        <Accordion sx={{ mt: 2 }} disableGutters>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#424242', fontSize: '1.3rem' }} />}
                sx={{
                    minHeight: 0,
                    backgroundColor: '#FAFAFA',
                    borderBottom: '1px solid #E0E0E0',
                    '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1.5 },
                }}
            >
                <Typography fontWeight={700} sx={{ fontSize: '0.95rem', color: '#212121' }}>Risk Indicators</Typography>
                <Chip
                    label={`${riskTypesCount} of 6 flagged`}
                    size="small"
                    sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        height: 20,
                        bgcolor: riskTypesCount > 0 ? '#FFF3E0' : '#E8F5E9',
                        color: riskTypesCount > 0 ? '#E65100' : '#2E7D32',
                        border: `1px solid ${riskTypesCount > 0 ? '#FFCC80' : '#A5D6A7'}`,
                        '& .MuiChip-label': { px: 1 },
                    }}
                />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
            <Paper elevation={0}>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table stickyHeader aria-label="customer health detail table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ borderBottom: 'none', position: 'sticky', left: 0, zIndex: 5, backgroundColor: '#fff', minWidth: 180, py: '6px' }} />
                            <TableCell
                                colSpan={accountData.customerProjects.length}
                                align="center"
                                sx={{
                                    py: '6px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    letterSpacing: '1.5px',
                                    color: '#9E9E9E',
                                    textTransform: 'uppercase',
                                    borderBottom: '1px solid #E0E0E0',
                                }}
                            >
                                Projects
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{
                                fontWeight: 'bold',
                                verticalAlign: 'middle',
                                paddingTop: 'auto',
                                paddingBottom: 'auto',
                                borderTop: 'none',
                                position: 'sticky',
                                left: 0,
                                zIndex: 5,
                                backgroundColor: 'white',
                                minWidth: 180,
                                 }}>Risk Indicator</TableCell>
                            {accountData.customerProjects.map(project => (
                                <TableCell key={project.sysId} sx={{ fontWeight: 'bold', minWidth: 250 }}>{project.name}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/*  Row for Recent Cases  */}
                        <TableRow>
                            <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>Cases Within Past 6 Months</TableCell>
                            {accountData.customerProjects.map(project => (
                                <RiskCell
                                    key={project.sysId + '-cases'}
                                    project={project}
                                    indicator="hasRecentCases"
                                    detailData={project.detailedRecentCases}
                                    renderDetailItem={(item: CaseGroup, index) => (
                                        <ListItem key={index} dense sx={{ pl: 4 }}>
                                            <ListItemText primary={`${item.priority} - ${item.count}`} />
                                        </ListItem>
                                    )}
                                />
                            ))}
                        </TableRow>
                        {/*  Row for Abandoned Migrations */}
                        <TableRow>
                            <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>Abandoned Product Migrations</TableCell>
                             {accountData.customerProjects.map(project => (
                                <RiskCell
                                    key={project.sysId + '-abandoned'}
                                    project={project}
                                    indicator="hasAbandonedCases"
                                    detailData={project.detailedAbandonedCases}
                                    renderDetailItem={(item: CaseLink, index) => (
                                        <ListItem key={index} dense sx={{ pl: 4 }}>
                                           <ListItemText primary={item.number} />
                                        </ListItem>
                                    )}
                                />
                            ))}
                        </TableRow>
                        {/*  Row for EOL Products */}
                         <TableRow>
                            <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>Using EOL Products</TableCell>
                             {accountData.customerProjects.map(project => (
                                <RiskCell
                                    key={project.sysId + '-eol'}
                                    project={project}
                                    indicator="isUsingEolProduct"
                                    detailData={project.softwareModel}
                                    renderDetailItem={(item: EolProduct, index) => (
                                        <ListItem key={index} dense sx={{ pl: 4, display: 'block' }}>
                                            <Typography variant="body2"><strong>Product:</strong> {item.name}</Typography>
                                            <Typography variant="body2"><strong>EOL Date:</strong> {item.eolDate}</Typography>
                                            {item.deployments && item.deployments.length > 0 && (
                                                <>
                                                 <Typography variant="body2"><strong>Deployments:</strong></Typography>
                                                 <List dense disablePadding sx={{ pl: 2 }}>
                                                     {item.deployments.map(dep => <ListItem key={dep.sysId} dense><ListItemText primary={dep.name} /></ListItem>)}
                                                 </List>
                                                </>
                                            )}
                                        </ListItem>
                                    )}
                                />
                            ))}
                        </TableRow>
                        {/* Row for Migration Delays */}
                         <TableRow>
                            <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>Migration Delays</TableCell>
                             {accountData.customerProjects.map(project => (
                                <RiskCell
                                    key={project.sysId + '-delays'}
                                    project={project}
                                    indicator="hasMigrationDelays"
                                    detailData={project.detailedMigrationDelays}
                                    renderDetailItem={(item: CaseLink, index) => (
                                        <ListItem key={index} dense sx={{ pl: 4 }}>
                                            <ListItemText primary={item.number} />
                                        </ListItem>
                                    )}
                                />
                            ))}
                        </TableRow>
                        {/*  Row for Recent Escalations */}
                         <TableRow>
                            <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>Recent Escalations (3mo)</TableCell>
                             {accountData.customerProjects.map(project => (
                                <RiskCell
                                    key={project.sysId + '-escalations'}
                                    project={project}
                                    indicator="hasEscalatedCases"
                                    detailData={project.detailedEscalatedCases}
                                    renderDetailItem={(item: CaseLink, index) => (
                                        <ListItem key={index} dense sx={{ pl: 4 }}>
                                            <ListItemText primary={item.escalationNumber} />
                                        </ListItem>
                                    )}
                                />
                            ))}
                        </TableRow>
                        {/*  Row for Go Live Status  */}
                        <TableRow>
                            <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>Go Live Status</TableCell>
                            {accountData.customerProjects.map(project => (
                                <RiskCell
                                    key={project.sysId + '-golive'}
                                    project={project}
                                    indicator="goLiveStatus"
                                    detailData={[]} 
                                    renderDetailItem={() => null}
                                />
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            </Paper>
            </AccordionDetails>
        </Accordion>
        <ActionItemsSection
            accountId={accountId}
            projects={accountData.customerProjects}
            healthStatusData={healthStatusData ?? []}
            onDataChanged={handleHealthStatusChanged}
            openAddForProject={addForProject}
            onAddForProjectConsumed={() => setAddForProject(null)}
        />
        </>
    );
};

// Status Indicator  
const StatusIndicator = ({ isRisk }: { isRisk: boolean }) => {
     // If it is a Risk, Red Cross
    if (isRisk) {
        return <CancelIcon sx={{ color: red[700], fontSize: 28 }} />;
    }
    // If it is NOT a Risk, Green Check
    return <CheckCircleIcon sx={{ color: green[700], fontSize: 28 }} />;
};
